import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../../../context/DarkModeContext';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { supabase } from '../../../utils/supabase';
import { IoAddOutline, IoRefreshOutline, IoBarChartOutline } from 'react-icons/io5';
import { motion, AnimatePresence } from 'framer-motion';
import MetricCard from './MetricCard';
import MetricsUpdateModal from './MetricsUpdateModal';
import MetricsHistory from './MetricsHistory';

interface MetricData {
  id: string;
  focus_id: string;
  metric_name: string;
  metric_value: number;
  current_value?: number;
  baseline_value?: number;
  target_value?: number;
  metric_unit?: string;
  metric_date: string;
  category?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface MetricsCardsProps {
  focusId: string;
}

const MetricsCards: React.FC<MetricsCardsProps> = ({ focusId }) => {
  const { darkMode } = useDarkMode();
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const [metrics, setMetrics] = useState<MetricData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<MetricData | undefined>();
  const [metricHistory, setMetricHistory] = useState<MetricData[]>([]);

  // Style classes
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';

  // Fetch metrics
  const fetchMetrics = async () => {
    if (!user || !focusId) return;

    try {
      // Get the latest value for each unique metric
      const { data, error } = await supabase
        .from('review_metrics')
        .select('*')
        .eq('focus_id', focusId)
        .order('metric_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Group by metric_name and take the latest entry for each
      const latestMetrics = data?.reduce((acc: MetricData[], metric) => {
        const existingIndex = acc.findIndex(m => m.metric_name === metric.metric_name);
        if (existingIndex === -1) {
          // Add current_value for compatibility
          acc.push({ ...metric, current_value: metric.metric_value });
        } else if (new Date(metric.metric_date) > new Date(acc[existingIndex].metric_date)) {
          // Replace with newer metric
          acc[existingIndex] = { ...metric, current_value: metric.metric_value };
        }
        return acc;
      }, []) || [];

      setMetrics(latestMetrics);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      showToast('Failed to load metrics', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch metric history
  const fetchMetricHistory = async (metricName: string) => {
    if (!user || !focusId) return;

    try {
      const { data, error } = await supabase
        .from('review_metrics')
        .select('*')
        .eq('focus_id', focusId)
        .eq('metric_name', metricName)
        .order('metric_date', { ascending: true });

      if (error) throw error;

      setMetricHistory(data || []);
    } catch (error) {
      console.error('Error fetching metric history:', error);
      showToast('Failed to load metric history', 'error');
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, [focusId, user]);

  // Save or update metric
  const handleSaveMetric = async (metricData: Partial<MetricData>) => {
    if (!user || !focusId) return;

    try {
      if (metricData.id) {
        // Update existing metric (create new entry with same name)
        const { error } = await supabase
          .from('review_metrics')
          .insert({
            focus_id: focusId,
            metric_name: metricData.metric_name,
            metric_value: metricData.current_value || metricData.metric_value || 0,
            baseline_value: metricData.baseline_value,
            target_value: metricData.target_value,
            metric_unit: metricData.metric_unit,
            category: metricData.category,
            notes: metricData.notes,
            metric_date: new Date().toISOString().split('T')[0]
          });

        if (error) throw error;
      } else {
        // Create new metric
        const { error } = await supabase
          .from('review_metrics')
          .insert({
            focus_id: focusId,
            metric_name: metricData.metric_name,
            metric_value: metricData.current_value || 0,
            baseline_value: metricData.baseline_value,
            target_value: metricData.target_value,
            metric_unit: metricData.metric_unit,
            category: metricData.category,
            notes: metricData.notes,
            metric_date: new Date().toISOString().split('T')[0]
          });

        if (error) throw error;
      }

      await fetchMetrics();
    } catch (error) {
      console.error('Error saving metric:', error);
      throw error;
    }
  };

  // Handle metric edit
  const handleEditMetric = (metric: MetricData) => {
    setSelectedMetric(metric);
    setShowUpdateModal(true);
  };

  // Handle view history
  const handleViewHistory = async (metric: MetricData) => {
    setSelectedMetric(metric);
    await fetchMetricHistory(metric.metric_name);
    setShowHistoryModal(true);
  };

  // Calculate trend for each metric
  const getMetricWithTrend = (metric: MetricData) => {
    // Simple trend calculation based on current vs baseline
    let trend: 'up' | 'down' | 'stable' = 'stable';
    if (metric.baseline_value) {
      const change = ((metric.current_value || metric.metric_value) - metric.baseline_value) / metric.baseline_value;
      if (change > 0.01) trend = 'up';
      else if (change < -0.01) trend = 'down';
    }
    return { ...metric, trend };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-bold ${textColor}`}>Performance Metrics</h2>
          <p className={`text-sm ${mutedTextColor} mt-1`}>
            Track and visualize your key performance indicators
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => fetchMetrics()}
            className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
            title="Refresh metrics"
          >
            <IoRefreshOutline className={`w-5 h-5 ${textColor}`} />
          </button>
          <button
            onClick={() => {
              setSelectedMetric(undefined);
              setShowUpdateModal(true);
            }}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <IoAddOutline className="w-5 h-5" />
            Add Metric
          </button>
        </div>
      </div>

      {/* Metrics Grid */}
      {metrics.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {metrics.map((metric, index) => (
              <motion.div
                key={metric.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <MetricCard
                  metric={getMetricWithTrend(metric)}
                  onEdit={() => handleEditMetric(metric)}
                  onViewHistory={() => handleViewHistory(metric)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        // Empty State
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`text-center py-12 px-6 rounded-xl border-2 border-dashed ${borderColor} ${cardBg}`}
        >
          <IoBarChartOutline className={`w-16 h-16 mx-auto mb-4 ${mutedTextColor}`} />
          <h3 className={`text-lg font-semibold ${textColor} mb-2`}>
            No metrics yet
          </h3>
          <p className={`${mutedTextColor} mb-6 max-w-md mx-auto`}>
            Start tracking your performance by adding key metrics that matter for your role and goals.
          </p>
          <button
            onClick={() => {
              setSelectedMetric(undefined);
              setShowUpdateModal(true);
            }}
            className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors inline-flex items-center gap-2"
          >
            <IoAddOutline className="w-5 h-5" />
            Add Your First Metric
          </button>
        </motion.div>
      )}

      {/* Modals */}
      <MetricsUpdateModal
        isOpen={showUpdateModal}
        onClose={() => {
          setShowUpdateModal(false);
          setSelectedMetric(undefined);
        }}
        metric={selectedMetric}
        onSave={handleSaveMetric}
      />

      <MetricsHistory
        isOpen={showHistoryModal}
        onClose={() => {
          setShowHistoryModal(false);
          setSelectedMetric(undefined);
          setMetricHistory([]);
        }}
        metricName={selectedMetric?.metric_name || ''}
        history={metricHistory}
        unit={selectedMetric?.metric_unit}
        target={selectedMetric?.target_value}
      />
    </div>
  );
};

export default MetricsCards;